import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import type { UserRole } from '../../../types/auth.types';
import AppShellSkeleton from '../AppShellSkeleton/AppShellSkeleton';
import { getDefaultRouteForRole } from '../../../utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requiresFeature?: string;
}

/**
 * ProtectedRoute Component
 * 
 * Route guard that enforces authentication and role-based access control
 * - Redirects to login if user is not authenticated
 * - Shows 403 error if user role is not in allowedRoles
 * - Stores intended destination for post-login redirect
 * 
 * @param children - The protected content to render
 * @param allowedRoles - Array of roles allowed to access this route (optional)
 *                       If not provided, any authenticated user can access
 * 
 * @example
 * // Protect route for authenticated users only
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 * 
 * // Protect route for specific roles
 * <ProtectedRoute allowedRoles={['SchoolAdmin', 'Receptionist']}>
 *   <Students />
 * </ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requiresFeature,
}) => {
  const { isAuthenticated, user, checkAuth, storeIntendedDestination } = useAuth();
  const { hasFeature, loading: featuresLoading } = useFeatureAccess();
  const location = useLocation();
  const [isChecking, setIsChecking] = React.useState(true);

  // Check authentication on mount
  useEffect(() => {
    const verifyAuth = async () => {
      await checkAuth();
      setIsChecking(false);
    };

    verifyAuth();
  }, [checkAuth]);

  // Show loading skeleton while checking authentication or features
  if (isChecking || (requiresFeature && featuresLoading)) {
    return <AppShellSkeleton />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Store the intended destination for post-login redirect
    storeIntendedDestination(location.pathname + location.search);
    
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Check for suspended/deactivated school status
  // SuperAdmins are exempt as their SchoolStatus is typically null/irrelevant
  if (user.schoolStatus === 'Suspended' && user.role !== 'SuperAdmin') {
    return <Navigate to="/suspended" replace />;
  }
  if (user.schoolStatus === 'Deactivated' && user.role !== 'SuperAdmin') {
    return <Navigate to="/deactivated" replace />;
  }

  // Check feature flag - redirect to dashboard if feature not enabled for this school
  if (requiresFeature && user?.role !== 'SuperAdmin' && !hasFeature(requiresFeature)) {
    const defaultRoute = getDefaultRouteForRole(user!.role);
    return <Navigate to={defaultRoute} replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    const hasPermission = allowedRoles.includes(user.role);

    if (!hasPermission) {
      // Get the default route for the user's role
      const defaultRoute = getDefaultRouteForRole(user.role);
      
      // Redirect to default route instead of showing 403 error
      return <Navigate to={defaultRoute} replace />;
    }
  }

  // User is authenticated and has permission
  return <>{children}</>;
};

export default ProtectedRoute;
